import axios from "axios";
import { resolveApiUrl } from "../../../assets/GlobalVariables";
import { getUserInfo, setUserInfo } from "../../../services/localStorage";
import { logout as agentLogout } from "../../../services/authService";
import {
  CORPORATE_ROLE_ADMIN,
  CORPORATE_USERTYPE,
} from "../constants/corporateConstants";
import { corporateFeAuthTrace } from "../utils/corporateFeTrace";

function readSessionId(user) {
  if (!user || typeof user !== "object") return null;
  return (
    user.sessionID ||
    user.sessionId ||
    (typeof localStorage !== "undefined" ? localStorage.getItem("sessionID") : null)
  );
}

function normalizeCorporateUser(payload) {
  const context = payload?.context || {};
  return {
    sessionID: payload?.sessionID || payload?.sessionId,
    email: payload?.email,
    usertype: payload?.usertype || CORPORATE_USERTYPE,
    user_type: context.user_type,
    company_id: context.company_id,
    company_user_id: context.company_user_id,
    company_name: context.company_name,
    permissions: context.permissions || [],
    context,
  };
}

export function persistCorporateUser(payload) {
  const user = normalizeCorporateUser(payload);
  setUserInfo(user);
  if (user.sessionID) {
    localStorage.setItem("sessionID", String(user.sessionID));
  }
  corporateFeAuthTrace("session persisted", {
    email: user.email,
    usertype: user.usertype,
    user_type: user.user_type,
    company_id: user.company_id,
  });
  return user;
}

function authHeaders(sessionId) {
  const sid = String(sessionId || "").trim();
  return {
    Authorization: `Bearer ${sid}`,
    "X-Session-ID": sid,
  };
}

export async function registerCompany(payload) {
  corporateFeAuthTrace("register request", { email: payload?.contact_email || payload?.email });
  const { data } = await axios.post(resolveApiUrl("/api/company/register"), payload, {
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
  });
  if (data?.ok && data?.sessionID) {
    persistCorporateUser(data);
  }
  return data;
}

export async function forgotCompanyPassword(email) {
  corporateFeAuthTrace("forgot password request", { email });
  const { data } = await axios.post(
    resolveApiUrl("/api/company/password/forgot"),
    { email },
    { withCredentials: true, headers: { "Content-Type": "application/json" } },
  );
  return data;
}

export async function resetCompanyPassword(token, password) {
  corporateFeAuthTrace("reset password request");
  const { data } = await axios.post(
    resolveApiUrl("/api/company/password/reset"),
    { token, password },
    { withCredentials: true, headers: { "Content-Type": "application/json" } },
  );
  return data;
}

export async function loginCompany(loginId, password) {
  corporateFeAuthTrace("login request", { email: loginId });
  const { data } = await axios.post(
    resolveApiUrl("/api/company/login"),
    { email: loginId, password },
    {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    },
  );
  if (data?.requires_2fa === true || data?.status === "requires_2fa") {
    return data;
  }
  if (data?.ok && data?.sessionID) {
    persistCorporateUser(data);
  }
  return data;
}

/**
 * Drop stale corporate identity from storage so login/dashboard cannot ping-pong
 * when /api/company/me returns 401 but UserInfo.usertype is still "corporate".
 */
export function clearCorporateSession() {
  corporateFeAuthTrace("clear session");
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("sessionID");
      const stored = getUserInfo();
      const ut = String(stored?.usertype || stored?.userType || "").toLowerCase();
      if (!stored || ut === CORPORATE_USERTYPE) {
        localStorage.removeItem("UserInfo");
      }
    }
  } catch {
    /* ignore storage errors */
  }
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("corporate-user-updated", { detail: { cleared: true } }));
    }
  } catch {
    /* ignore */
  }
}

export async function getCompanyMe() {
  const stored = getUserInfo();
  const sessionId = readSessionId(stored);
  if (!sessionId) {
    corporateFeAuthTrace("getCompanyMe skipped — no session");
    return { ok: false, error: "unauthorized" };
  }

  try {
    const { data } = await axios.get(resolveApiUrl("/api/company/me"), {
      withCredentials: true,
      headers: authHeaders(sessionId),
      params: { sessionID: sessionId },
    });

    if (data?.ok) {
      persistCorporateUser(data);
    } else if (data?.error === "unauthorized") {
      clearCorporateSession();
    }
    corporateFeAuthTrace("getCompanyMe response", {
      ok: data?.ok,
      user_type: data?.context?.user_type,
      company_id: data?.context?.company_id,
    });
    return data;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      corporateFeAuthTrace("getCompanyMe unauthorized — clearing session", { status });
      clearCorporateSession();
      return { ok: false, error: "unauthorized" };
    }
    throw err;
  }
}

export function isCorporateUser(user) {
  const u = user || getUserInfo();
  if (!u || typeof u !== "object") return false;
  return String(u.usertype || u.userType || "").toLowerCase() === CORPORATE_USERTYPE;
}

/** Corporate identity with a local session id — safe for auto-redirect to portal. */
export function hasCorporateSession(user) {
  const u = user || getUserInfo();
  return isCorporateUser(u) && Boolean(readSessionId(u));
}

export function isCorporateAdmin(user) {
  const u = user || getUserInfo();
  if (!isCorporateUser(u)) return false;
  const role = u.context?.user_type || u.user_type;
  return String(role || "").toLowerCase() === CORPORATE_ROLE_ADMIN;
}

export async function logoutCompany() {
  corporateFeAuthTrace("logout");
  try {
    await agentLogout();
  } finally {
    clearCorporateSession();
  }
}
